import React, { useState } from "react";
import { Modal, Button, Progress, message } from "antd";
import {
  LoadingOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { uploadFile } from "../services/filesAPI";
import FileUpload from "./FileUpload";

const FileUploadModal = ({
  isOpen,
  onClose,
  projectId,
  onUploadSuccess,
  title = "Upload File",
  description = "Upload your files here. We accept various file formats.",
  accept = "*/*",
  maxSize = 262144000,
  multiple = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { mutate, isPending } = useMutation({
    mutationFn: async (files) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });
      return uploadFile(projectId, formData, (progress) => {
        setUploadProgress(progress);
      });
    },
    onSuccess: (data) => {
      message.success(data.message);
      onUploadSuccess?.(data.files);
      setSelectedFiles([]);
      setUploadProgress(0);
      onClose();
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || "Failed to upload files");
      setUploadProgress(0);
    },
  });

  const handleFileSelect = (files) => {
    // Handle both single file and array of files
    const newFiles = Array.isArray(files) ? files : [files];
    setSelectedFiles((prevFiles) => {
      if (!multiple) {
        // For single file mode, replace the existing file
        return newFiles;
      }
      // For multiple file mode, add to existing files
      const existingFileNames = new Set(prevFiles.map((f) => f.name));
      const uniqueNewFiles = newFiles.filter(
        (file) => !existingFileNames.has(file.name),
      );
      return [...prevFiles, ...uniqueNewFiles];
    });
    setUploadProgress(0);
  };

  const handleDeleteFile = (indexToDelete) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToDelete),
    );
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;
    setUploadProgress(0);
    mutate(selectedFiles);
  };

  const handleClose = () => {
    if (!isPending) {
      setSelectedFiles([]);
      setUploadProgress(0);
      onClose();
    }
  };

  // Calculate total size and uploaded size for multiple files
  const getTotalSize = () => {
    return selectedFiles.reduce((total, file) => total + file.size, 0);
  };

  const getUploadedSize = () => {
    return Math.round((getTotalSize() * uploadProgress) / 100);
  };

  // Format bytes to human readable format
  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      title={title}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || isPending}
          icon={isPending ? <LoadingOutlined /> : <UploadOutlined />}
        >
          {isPending ? "Uploading" : "Upload"}
        </Button>,
      ]}
      width={640}
    >
      <p className="text-gray-500 mb-4">{description}</p>

      <FileUpload
        onUpload={handleFileSelect}
        accept={accept}
        maxSize={maxSize}
        multiple={multiple}
      />

      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <p className="text-gray-700 font-medium">Selected Files:</p>
          <ul className="mt-2">
            {selectedFiles.map((file, index) => (
              <li
                key={index}
                className="flex items-center justify-between py-1 text-gray-600"
              >
                <span className="truncate flex-1">
                  {file.name} ({formatBytes(file.size)})
                </span>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteFile(index)}
                  className="text-red-500 hover:text-red-700"
                  disabled={isPending}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {isPending && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <LoadingOutlined className="text-blue-500" />
              <span className="text-gray-600">
                Uploading... {uploadProgress}%
              </span>
            </div>
            <span className="text-gray-600 text-sm">
              {formatBytes(getUploadedSize())} / {formatBytes(getTotalSize())}
            </span>
          </div>
          <Progress
            percent={uploadProgress}
            status={uploadProgress === 100 ? "success" : "active"}
            strokeColor={{
              from: "#108ee9",
              to: "#87d068",
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default FileUploadModal;
